import {inject, Injectable} from "@angular/core";
import {environment} from "../../../../environments/environment";
import {SpecifModifs} from "../models/specif_modifs.model";
import {map, Observable} from "rxjs";
import {HttpClient, HttpParams} from "@angular/common/http";
import {SpecificationEtape, SpecificationEtapeMaster,} from "../models/specification-etape.model";
import {EtapePathDoer} from "../models/etape-path.model";
import {ProcessEtape} from "../../process-etape/models/process_etape.model";
import {EntityApiService} from "../../../shared/store/entity-store/entity-api.service";
import {QueryParam} from "app/shared/store/store-types.model";
import {queryParamToHttp} from "../../../shared/store/utils/store-utils.service";
import {EtatSpecification} from "../enums/etat-specif.enum";
import {safeFirstValueFrom} from "../../../shared/utils/safe-first-value-from";
import {NomenclatureLigne} from "../../nomenclature/models/nomenclature_ligne.model";
import {SpecifImportPayload} from "../../articles/models/article-payload-duplicate.model";

@Injectable({
  providedIn: "root",
})
export class SpecificationApiService
  implements EntityApiService<SpecificationEtapeMaster>
{
  loadAll(query?: QueryParam): Observable<SpecificationEtapeMaster[]> {
    let params = queryParamToHttp(query);
    return this.http.get<SpecificationEtapeMaster[]>(
      `${environment.api()}/specification_etape/master`,
      { params },
    );
  }

  loadAllFilter(filters: {
    page?: number;
    search?: string;
    etatSpecification?: EtatSpecification[];
    sousFamille?: string[];
    famille?: string[];
    others?: string[];
    modelSpecificationEtapeMasterId?: string;
    type?: 'ARTICLE' | 'SPECIFICATION';
  }) {
    let params = new HttpParams();
    if (filters.type) {
      params = params.set("type", filters.type);
    }
    if (filters.modelSpecificationEtapeMasterId) {
      params = params.set(
        "modelSpecificationEtapeMasterId",
        filters.modelSpecificationEtapeMasterId,
      );
    }
    if (filters.page !== null && filters.page !== undefined) {
      params = params.set("page", filters.page);
    }
    if (filters.etatSpecification?.length) {
      filters.etatSpecification.forEach((etat) => {
        params = params.append("etatSpecification", etat.toString());
      });
    }
    if (filters.sousFamille?.length) {
      filters.sousFamille.forEach((ligne) => {
        params = params.append("sousFamille", ligne);
      });
    }
    if (filters.famille?.length) {
      filters.famille.forEach((ligne) => {
        params = params.append("famille", ligne);
      });
    }
    if (filters.search) {
      params = params.set("search", filters.search);
    }
    if (filters.others?.length) {
      if (filters.others.includes("WITHSPECIF")) {
        params = params.set("withSpecif", true);
      }
      if (filters.others.includes("ARCHIVED")) {
        params = params.set("archived", true);
      }
    }

    return this.http.get<SpecificationEtapeMaster[]>(
      `${environment.api()}/specification_etape/master`,
      {
        params,
      },
    );
  }

  loadAllModelsFilter(filters: {
    page?: number;
    search?: string;
    etats?: EtatSpecification[];
    archived?: boolean;
  }) {
    let params = new HttpParams();
    if (filters.page !== null && filters.page !== undefined) {
      params = params.set("page", filters.page);
    }
    if (filters.search) {
      params = params.set("search", filters.search);
    }
    if (filters.etats?.length) {
      filters.etats.forEach((etat) => {
        params = params.append("etatSpecification", etat);
      });
    }
    if (filters.archived) {
      params = params.set("archived", true);
    }

    return this.http.get<SpecificationEtapeMaster[]>(
      `${environment.api()}/specification_etape/parents`,
      { params },
    );
  }

  create(
    entity: SpecificationEtapeMaster,
    query?: QueryParam,
  ): Observable<SpecificationEtapeMaster> {
    throw new Error("Method not implemented.");
  }

  createMasterWithEtape(
    etape: SpecificationEtape,
  ): Observable<SpecificationEtapeMaster> {
    return this.http.post<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/create_master`,
      etape,
    );
  }

  remove(id: string): Observable<string> {
    return this.http
      .delete(`${environment.api()}/specification_etape/master/${id}`)
      .pipe(map((r) => id));
  }

  canDelete(id: string): Observable<{ canDelete: boolean }> {
    return this.http.get<{ canDelete: boolean }>(
      `${environment.api()}/specification_etape/master/${id}/can-delete`,
    );
  }

  archive(id: string): Observable<SpecificationEtapeMaster> {
    return this.http.post<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/master/${id}/archive`,
      null,
    );
  }

  unArchive(id: string): Observable<SpecificationEtapeMaster> {
    return this.http.post<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/master/${id}/unarchive`,
      null,
    );
  }

  removeSpecificationEtape(seId: string): Observable<SpecificationEtapeMaster> {
    return this.http.delete<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/seId/${seId}`,
    );
  }

  update(
    entity: SpecificationEtapeMaster,
  ): Observable<SpecificationEtapeMaster> {
    return this.http.post<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/master/${entity.id}`,
      entity,
    );
  }

  loadOne(
    id: string,
    query?: QueryParam,
  ): Observable<SpecificationEtapeMaster> {
    return this.http.get<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/master/${id}`,
    );
  }

  loadByArticleId(
    id: string,
    query?: QueryParam,
  ): Observable<SpecificationEtapeMaster> {
    return this.http.get<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/master/article/${id}`,
    );
  }

  private http = inject(HttpClient);

  updateSe(
    entity: SpecificationEtape,
  ): Observable<SpecificationEtapeMaster> {
    return this.http.post<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/update-se/${entity.id}`,
      entity,
    );
  }

  async loadspecifModifs(masterId: string) {
    return await safeFirstValueFrom(
      this.http.get<SpecifModifs[]>(
        `${environment.api()}/specif_modifs/semId/${masterId}`,
      ),
    );
  }

  async updateDocumentListeId(semId: string, documentListId: string) {
    return await safeFirstValueFrom(
      this.http.post(
        `${environment.api()}/specification_etape/documentList/${semId}/${documentListId}`,
        null,
      ),
    );
  }

  async loadSEMByIdEagerWithParents(masterId: string) {
    return await safeFirstValueFrom(
      this.http.get<SpecificationEtapeMaster>(
        `${environment.api()}/specification_etape/id/` +
          masterId +
          "/eager/parents",
      ),
    );
  }

  async createNewSpeficicationEtape(master_id: string, articleId: string) {
    return await safeFirstValueFrom(
      this.http.post<SpecificationEtape>(
        `${environment.api()}/specification_etape/new/${master_id}/articleId/${articleId}`,
        null,
      ),
    );
  }

  async updatePhoto(infosArticleId: string, photo: string) {
    return await safeFirstValueFrom(
      this.http.post<SpecificationEtape>(
        `${environment.api()}/specification_etape/infosArticle/${infosArticleId}/photo/${photo}`,
        null,
      ),
    );
  }

  async endRedaction(etapeId: string) {
    return await safeFirstValueFrom(
      this.http.post<SpecificationEtape>(
        `${environment.api()}/specification_etape/end_redaction/${etapeId}`,
        null,
      ),
    );
  }

  async validate(etapeId: string) {
    return await safeFirstValueFrom(
      this.http.post<SpecificationEtape>(
        `${environment.api()}/specification_etape/validate/${etapeId}`,
        null,
      ),
    );
  }

  async approuve(etapeId: string) {
    return await safeFirstValueFrom(
      this.http.post<SpecificationEtape>(
        `${environment.api()}/specification_etape/approuve/${etapeId}`,
        null,
      ),
    );
  }

  async reindexProcessEtapes(ps: ProcessEtape[], specificationEtapeId: string) {
    return await safeFirstValueFrom(
      this.http.post<SpecificationEtape>(
        `${environment.api()}/specification_etape/reindex_process_etapes/${specificationEtapeId}`,
        ps,
      ),
    );
  }

  loadSEMModels() {
    return this.http.get<SpecificationEtapeMaster[]>(
      `${environment.api()}/specification_etape/models`,
    );
  }

  loadAllSpecifModelsForSelection() {
    return this.http.get<SpecificationEtapeMaster[]>(
      `${environment.api()}/specification_etape/models/for_selection`,
    );
  }

  updateSEModel(
    specificationEtapeId: string,
    modelSpecificationEtapeId: string,
  ) {
    return this.http.post<SpecificationEtapeMaster>(
      `${environment.api()}/specification_etape/update-article-model`,
      {
        specificationEtapeId,
        modelSpecificationEtapeId,
      },
    );
  }

  loadSemModelLinked(id: string) {
    return this.http.get<SpecificationEtapeMaster[]>(
      `${environment.api()}/specification_etape/models/${id}/sems`,
    );
  }

  updatePodIdForEtape(
    specificationEtapeId: string,
    podId: string,
    key: string,
  ) {
    return this.http.post(
      `${environment.api()}/specification_etape/update_pod_id/${specificationEtapeId}/${podId}/${key}`,
      null,
    );
  }

  saveSpecifModifs(specifModifs: SpecifModifs) {
    return this.http.post<SpecifModifs>(
      `${environment.api()}/specif_modifs`,
      specifModifs,
    );
  }

  doneSpecifModifs(specifModifs: SpecifModifs) {
    return this.http.post<SpecifModifs>(
      `${environment.api()}/specif_modifs/done`,
      specifModifs,
    );
  }

  loadPathDoer(articleId: string, specificationEtapeId: string) {
    let params = queryParamToHttp({ specificationEtapeId });
    return this.http.get<EtapePathDoer>(
      `${environment.api()}/specification_etape/path-doer/${articleId}`,
      { params },
    );
  }

  finishOcr(payload: {
    processes: ProcessEtape[];
    nomenclatureLignes: NomenclatureLigne[];
    denominationModel: string;
    denominationArticle: string;
  }) {
    return this.http.post(
      `${environment.api()}/specification_etape/from_ocr`,
      payload,
    );
  }

  importFromArticle(payload: SpecifImportPayload) {
    return this.http.post<SpecificationEtapeMaster>(
      `${environment.api()}/articles/import_article`,
      payload,
    );
  }
}
